import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Alert, InputAdornment, CircularProgress, FormControlLabel, Checkbox, Divider } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { connectInstanceService, ConnectInstance, ConnectInstanceCreate } from '../../services/connectInstanceService';
import { useUser } from '../../contexts/UserContext';
import { Navigate } from 'react-router-dom';
import SSHLogDrawer from '../../components/admin/SSHLogDrawer';

const ConnectInstances: React.FC = () => {
  const { isAdmin, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<ConnectInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creationModalOpen, setCreationModalOpen] = useState(false);
  const [creatingInstanceId, setCreatingInstanceId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { clients } = useUser();
  
  // A record check and creation modal state
  const [aRecordModalOpen, setARecordModalOpen] = useState(false);
  const [dnsInstructionsModalOpen, setDnsInstructionsModalOpen] = useState(false);
  const [checkingARecord, setCheckingARecord] = useState(false);
  const [aRecordInfo, setARecordInfo] = useState<{
    domain: string;
    isManaged: boolean;
    exists: boolean;
    currentIp?: string;
    ipMatches?: boolean;
    canManage?: boolean;
    resolvedIp?: string;
    availableZones?: string[];
  } | null>(null);
  const [aRecordCheckResult, setARecordCheckResult] = useState<any>(null);
  const [aRecordIp, setARecordIp] = useState('18.210.165.240');
  const [creatingARecord, setCreatingARecord] = useState(false);

  // Email configuration fields
  const [sendingEmailName, setSendingEmailName] = useState('');
  const [sendingEmailAddress, setSendingEmailAddress] = useState('');

  // Domain management options
  const [createDnsRecord, setCreateDnsRecord] = useState(true);
  const [configureSes, setConfigureSes] = useState(false);
  const [emailDomainInfo, setEmailDomainInfo] = useState<{
    domain: string;
    isManaged: boolean;
    canManage: boolean;
  } | null>(null);
  const [checkingEmailDomain, setCheckingEmailDomain] = useState(false);

  // Connect URL domain info (for DNS checkbox)
  const [connectDomainInfo, setConnectDomainInfo] = useState<{
    domain: string;
    isManaged: boolean;
    canManage: boolean;
  } | null>(null);
  const [checkingConnectDomain, setCheckingConnectDomain] = useState(false);

  useEffect(() => {
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const data = await connectInstanceService.getAll();
      setInstances(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch connect instances', err);
      setError(err.message || 'Failed to load connect instances');
    } finally {
      setLoading(false);
    }
  };

  const checkARecord = async (domainToCheck: string): Promise<boolean> => {
    if (!domainToCheck.trim()) {
      return true; // Proceed if no domain
    }

    try {
      setCheckingARecord(true);
      const result = await connectInstanceService.checkARecord(domainToCheck.trim());
      
      // Check if A record exists and points to correct IP (18.210.165.240)
      const ipMatches = result.ip_matches === true;
      const recordExists = result.a_record_exists === true;
      
      // If IP matches and record exists, proceed
      if (recordExists && ipMatches) {
        return true; // Proceed with creation
      }
      
      // Store the full result for debugging
      setARecordCheckResult(result);
      
      // If domain is managed in Route53 and we can manage it
      if (result.is_managed_in_route53 && result.can_manage) {
        setARecordInfo({
          domain: domainToCheck.trim(),
          isManaged: true,
          exists: recordExists,
          currentIp: result.current_ip,
          ipMatches: ipMatches,
          canManage: true,
          resolvedIp: result.resolved_ip,
          availableZones: result.available_zones
        });
        setARecordModalOpen(true);
        return false; // Don't proceed with creation yet
      }
      
      // If domain is not managed in Route53 or we can't manage it, show DNS instructions
      setARecordInfo({
        domain: domainToCheck.trim(),
        isManaged: result.is_managed_in_route53 || false,
        exists: recordExists,
        currentIp: result.current_ip,
        ipMatches: ipMatches,
        canManage: result.can_manage || false,
        resolvedIp: result.resolved_ip,
        availableZones: result.available_zones
      });
      setDnsInstructionsModalOpen(true);
      return false; // Don't proceed with creation yet
    } catch (err: any) {
      console.error('Failed to check A record', err);
      setError(err.response?.data?.error || err.message || 'Failed to check A record');
      return false; // Don't proceed on error
    } finally {
      setCheckingARecord(false);
    }
  };

  const handleCreateARecord = async () => {
    if (!aRecordInfo) return;

    try {
      setCreatingARecord(true);
      const result = await connectInstanceService.createARecord(aRecordInfo.domain, aRecordIp);
      
      if (result.success) {
        setARecordModalOpen(false);
        setARecordInfo(null);
        // Wait a moment for DNS propagation, then recheck
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Recheck the A record to verify it was created correctly
        const canProceed = await checkARecord(aRecordInfo.domain);
        if (canProceed) {
          // Now proceed with instance creation
          await proceedWithInstanceCreation();
        }
      } else {
        setError(result.error || 'Failed to create A record');
      }
    } catch (err: any) {
      console.error('Failed to create A record', err);
      setError(err.response?.data?.error || err.message || 'Failed to create A record');
    } finally {
      setCreatingARecord(false);
    }
  };

  const handleRecheckARecord = async () => {
    if (!aRecordInfo) return;
    setCheckingARecord(true);
    try {
      const canProceed = await checkARecord(aRecordInfo.domain);
      if (canProceed) {
        setDnsInstructionsModalOpen(false);
        setARecordInfo(null);
        await proceedWithInstanceCreation();
      }
    } catch (err: any) {
      console.error('Failed to recheck A record', err);
      setError(err.response?.data?.error || err.message || 'Failed to recheck A record');
    } finally {
      setCheckingARecord(false);
    }
  };

  // Extract domain from email address and check if it's in Route53
  const checkEmailDomainInRoute53 = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailDomainInfo(null);
      return;
    }

    const emailDomain = email.split('@')[1];
    if (!emailDomain) {
      setEmailDomainInfo(null);
      return;
    }

    try {
      setCheckingEmailDomain(true);
      const result = await connectInstanceService.checkEmailDomain(emailDomain);

      setEmailDomainInfo({
        domain: emailDomain,
        isManaged: result.is_managed_in_route53,
        canManage: result.can_manage || false,
      });

      // Auto-enable SES configuration if domain is managed
      if (result.is_managed_in_route53 && result.can_manage) {
        setConfigureSes(true);
      }
    } catch (err: any) {
      console.error('Failed to check email domain', err);
      setEmailDomainInfo(null);
    } finally {
      setCheckingEmailDomain(false);
    }
  };

  // Handle email address change with debounced domain check
  const handleEmailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setSendingEmailAddress(newEmail);

    // Debounce the domain check
    const timeoutId = setTimeout(() => {
      checkEmailDomainInRoute53(newEmail);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Check if connect URL domain is in Route53
  const checkConnectDomainInRoute53 = async (connectDomain: string) => {
    if (!connectDomain || !connectDomain.includes('.')) {
      setConnectDomainInfo(null);
      return;
    }

    try {
      setCheckingConnectDomain(true);
      const result = await connectInstanceService.checkEmailDomain(connectDomain);

      setConnectDomainInfo({
        domain: connectDomain,
        isManaged: result.is_managed_in_route53,
        canManage: result.can_manage || false,
      });

      // Auto-enable DNS creation if domain is managed
      if (result.is_managed_in_route53 && result.can_manage) {
        setCreateDnsRecord(true);
      }
    } catch (err: any) {
      console.error('Failed to check connect domain', err);
      setConnectDomainInfo(null);
    } finally {
      setCheckingConnectDomain(false);
    }
  };

  // Handle domain change with debounced check
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDomain = e.target.value;
    setDomain(newDomain);

    // Debounce the domain check
    const timeoutId = setTimeout(() => {
      checkConnectDomainInRoute53(newDomain.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const proceedWithInstanceCreation = async () => {
    if (!selectedClientId || !domain) {
      setError('Please select a client and enter a domain');
      return;
    }

    try {
      setError(null);
      const newInstance = await connectInstanceService.create({
        client_id: Number(selectedClientId),
        domain: domain.trim(),
        sending_email_name: sendingEmailName.trim() || undefined,
        sending_email_address: sendingEmailAddress.trim() || undefined,
        create_dns_record: createDnsRecord,
        configure_ses: configureSes && emailDomainInfo?.isManaged,
      });
      setInstances([...instances, newInstance]);
      setCreateDialogOpen(false);
      setSelectedClientId('');
      setDomain('');
      setARecordIp('18.210.165.240'); // Reset IP
      setSendingEmailName('');
      setSendingEmailAddress('');
      setCreateDnsRecord(true);
      setConfigureSes(false);
      setEmailDomainInfo(null);

      // Open creation modal to show progress
      setCreatingInstanceId(newInstance.id);
      setCreationModalOpen(true);
    } catch (err: any) {
      console.error('Failed to create connect instance', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create connect instance');
    }
  };

  const handleCreate = async () => {
    if (!selectedClientId || !domain) {
      setError('Please select a client and enter a domain');
      return;
    }

    // Check A record first
    const canProceed = await checkARecord(domain);
    if (!canProceed) {
      // Modal will be shown, don't proceed yet
      return;
    }

    // Proceed with creation
    await proceedWithInstanceCreation();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter instances based on search query
  const filteredInstances = useMemo(() => {
    if (!searchQuery.trim()) {
      return instances;
    }

    const query = searchQuery.toLowerCase().trim();
    return instances.filter((instance) => {
      // Search in domain
      if (instance.domain?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in client name
      const client = clients.find(c => c.id === instance.client_id);
      if (client?.agency_name?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in status
      if (instance.status?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in username
      if (instance.username?.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }, [instances, searchQuery, clients]);

  const columns: GridColDef[] = [
    {
      field: 'domain',
      headerName: 'Domain',
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="text"
          onClick={() => {
            // Store current selectedClientId in sessionStorage before navigating
            if (selectedClientId) {
              sessionStorage.setItem('connectInstanceClientId', selectedClientId.toString());
            }
            navigate(`/app/admin/connect-instances/${params.row.id}`);
          }}
          sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'client_id',
      headerName: 'Client',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const client = clients.find(c => c.id === params.value);
        return client ? client.agency_name : `Client ${params.value}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 150,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4">Connect Instances</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Instance
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search by domain, client name, status, or username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 600 }}
        />
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredInstances.length} of {instances.length} instances
          </Typography>
        )}
      </Box>

      <Paper sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filteredInstances}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50 },
            },
          }}
          onRowClick={(params) => {
            // Store current selectedClientId in sessionStorage before navigating
            if (selectedClientId) {
              sessionStorage.setItem('connectInstanceClientId', selectedClientId.toString());
            }
            navigate(`/app/admin/connect-instances/${params.row.id}`);
          }}
          sx={{ cursor: 'pointer', flex: 1, minHeight: 0 }}
        />
      </Paper>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Connect Instance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Client</InputLabel>
              <Select
                value={selectedClientId}
                label="Client"
                onChange={(e) => setSelectedClientId(e.target.value as number)}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.agency_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Domain"
              value={domain}
              onChange={handleDomainChange}
              placeholder="example.traveloffers.info"
              helperText="Enter the domain for the connect instance (e.g., test1.traveloffers.info)"
              disabled={checkingARecord}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (checkingARecord || checkingConnectDomain) ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Email Configuration</Typography>

            <TextField
              fullWidth
              label="Sending Email Name"
              value={sendingEmailName}
              onChange={(e) => setSendingEmailName(e.target.value)}
              placeholder="John's Travel Agency"
              helperText="The name that will appear in the 'From' field of emails"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Sending Email Address"
              value={sendingEmailAddress}
              onChange={handleEmailAddressChange}
              placeholder="info@example.com"
              helperText="The email address that will be used to send emails"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: checkingEmailDomain ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }}
            />

            {(connectDomainInfo || emailDomainInfo) && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Domain Configuration Options</Typography>

                {connectDomainInfo && (
                  connectDomainInfo.isManaged ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      The connect domain <strong>{connectDomainInfo.domain}</strong> is managed in our AWS account.
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      The connect domain <strong>{connectDomainInfo.domain}</strong> is not managed in our AWS account.
                      DNS will need to be configured manually.
                    </Alert>
                  )
                )}

                {connectDomainInfo?.isManaged && connectDomainInfo?.canManage && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createDnsRecord}
                        onChange={(e) => setCreateDnsRecord(e.target.checked)}
                      />
                    }
                    label="Create DNS A record for connect URL"
                  />
                )}

                {emailDomainInfo && (
                  emailDomainInfo.isManaged ? (
                    <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
                      The email domain <strong>{emailDomainInfo.domain}</strong> is managed in our AWS account.
                    </Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                      The email domain <strong>{emailDomainInfo.domain}</strong> is not managed in our AWS account.
                      SES configuration will need to be done manually.
                    </Alert>
                  )
                )}

                {emailDomainInfo?.isManaged && emailDomainInfo?.canManage && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={configureSes}
                        onChange={(e) => setConfigureSes(e.target.checked)}
                      />
                    }
                    label={`Configure SES for ${emailDomainInfo.domain} (DKIM, SPF, bounce/complaint handling)`}
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setDomain('');
            setSendingEmailName('');
            setSendingEmailAddress('');
            setCreateDnsRecord(true);
            setConfigureSes(false);
            setEmailDomainInfo(null);
            setConnectDomainInfo(null);
            setARecordInfo(null);
            setARecordModalOpen(false);
            setDnsInstructionsModalOpen(false);
          }}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!selectedClientId || !domain || checkingARecord || checkingEmailDomain || checkingConnectDomain}
          >
            {checkingARecord || checkingEmailDomain || checkingConnectDomain ? 'Checking...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* A Record Creation Modal - When we can manage Route53 */}
      <Dialog 
        open={aRecordModalOpen} 
        onClose={() => {
          if (!creatingARecord && !checkingARecord) {
            setARecordModalOpen(false);
            setARecordInfo(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create A Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {aRecordInfo?.exists && !aRecordInfo?.ipMatches ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The domain <strong>{aRecordInfo?.domain}</strong> has an A record pointing to <strong>{aRecordInfo?.currentIp}</strong>, 
                but it needs to point to <strong>18.210.165.240</strong> (mapserv).
                Would you like to update it?
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                The domain <strong>{aRecordInfo?.domain}</strong> is managed in Route53, but no A record exists or it doesn't point to the correct IP.
                Would you like to create/update one now?
              </Alert>
            )}
            <TextField
              fullWidth
              label="A Record IP Address"
              value={aRecordIp}
              onChange={(e) => setARecordIp(e.target.value)}
              placeholder="18.210.165.240"
              helperText={`A record for ${aRecordInfo?.domain} will point to this IP address (mapserv)`}
              disabled={creatingARecord || checkingARecord}
              sx={{ mt: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              The A record will be created/updated for: <strong>{aRecordInfo?.domain}</strong>
            </Typography>
            {aRecordInfo?.resolvedIp && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Current DNS resolution: <strong>{aRecordInfo.resolvedIp}</strong>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setARecordModalOpen(false);
              setARecordInfo(null);
            }}
            disabled={creatingARecord || checkingARecord}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateARecord} 
            variant="contained"
            disabled={creatingARecord || checkingARecord || !aRecordIp.trim()}
          >
            {creatingARecord ? 'Creating...' : checkingARecord ? 'Checking...' : 'Create/Update A Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DNS Instructions Modal - When we can't manage Route53 */}
      <Dialog 
        open={dnsInstructionsModalOpen} 
        onClose={() => {
          if (!checkingARecord) {
            setDnsInstructionsModalOpen(false);
            setARecordInfo(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>DNS Configuration Required</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The domain <strong>{aRecordInfo?.domain}</strong> must have an A record pointing to <strong>18.210.165.240</strong> before creating the instance.
            </Alert>
            
                {aRecordInfo?.isManaged ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                This domain is managed in Route53, but we don't have permissions to edit it.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  This domain is not managed in Route53. You'll need to create the DNS record manually.
                </Typography>
                {aRecordInfo?.availableZones && aRecordInfo.availableZones.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" component="div">
                      <strong>Available Route53 zones:</strong>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {aRecordInfo.availableZones.map((zone, idx) => (
                          <li key={idx}>{zone}</li>
                        ))}
                      </ul>
                    </Typography>
                  </Alert>
                )}
              </>
            )}
            
            <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Required DNS Record:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', mb: 1 }}>
                Type: A{'\n'}
                Name: {aRecordInfo?.domain}{'\n'}
                Value: 18.210.165.240{'\n'}
                TTL: 300 (or default)
              </Typography>
            </Box>

            {aRecordInfo?.resolvedIp && (
              <Alert severity={aRecordInfo.ipMatches ? 'success' : 'info'} sx={{ mb: 2 }}>
                Current DNS resolution: <strong>{aRecordInfo.resolvedIp}</strong>
                {aRecordInfo.ipMatches ? ' ✓ Correct IP' : ' ✗ Incorrect IP'}
              </Alert>
            )}

            {aRecordInfo?.currentIp && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Route53 A record IP: <strong>{aRecordInfo.currentIp}</strong>
                {aRecordInfo.ipMatches ? ' ✓' : ' ✗'}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              After creating the DNS record, click "Recheck" to verify it's configured correctly.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDnsInstructionsModalOpen(false);
              setARecordInfo(null);
            }}
            disabled={checkingARecord}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRecheckARecord} 
            variant="contained"
            disabled={checkingARecord}
          >
            {checkingARecord ? 'Checking...' : 'Recheck'}
          </Button>
        </DialogActions>
      </Dialog>

      <SSHLogDrawer
        open={creationModalOpen}
        instanceId={creatingInstanceId}
        operationType={null}
        onClose={() => {
          setCreationModalOpen(false);
          setCreatingInstanceId(null);
          // Refresh instances list
          fetchInstances();
        }}
      />
    </Box>
  );
};

export default ConnectInstances;


